<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import { toggleActions } from "@/action/toggleActions";
import { actionSourceKey } from "@/components/action/injectKeys";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import ToggleButton from "@/components/ui/ToggleButton/ToggleButton.vue";
import ToggleItem from "@/components/ui/ToggleButton/ToggleItem.vue";
import { useShortcuts } from "@/composables/useShortcuts";
import { useWorkModeStore } from "@/store/workMode";
import { injectStrict } from "@/utils/vue";

const { isExecutionMode } = storeToRefs(useWorkModeStore());
const { t } = useI18n();
const actionSource = injectStrict(actionSourceKey);
const { registerShortcut } = useShortcuts();

const props = defineProps<{ isTopLevel?: boolean }>();

const tooltip = computed(() =>
  t("action.switchWorkMode", {
    mode: isExecutionMode.value ? t("action.planMode") : t("action.executionMode"),
  }),
);

onMounted(() => {
  if (props.isTopLevel) {
    registerShortcut(toggleActions.toggleWorkMode, { prevent: true });
  }
});
</script>

<template>
  <ToggleButton
    class="mode-toggle-button"
    :model-value="useWorkModeStore().workingMode"
    :aria-label="tooltip"
    @update:model-value="toggleActions.toggleWorkMode(actionSource)"
  >
    <template #tooltip>
      {{ tooltip }}
      <span class="shortcut">
        {{ toggleActions.toggleWorkMode.data.shortcut.name() }}
      </span>
    </template>
    <ToggleItem value="planning">
      <SvgIcon name="general/pi" height="20" width="20" />
    </ToggleItem>
    <ToggleItem value="execution">
      <SvgIcon name="general/progress" height="20" width="20" />
    </ToggleItem>
  </ToggleButton>
</template>
